import { useCallback } from 'react';
import { CreatableOption, SelectProps, OptionTypeWithCode } from '../../types';
import { isArray } from '../../../../utils/guards';
import { isCreatableOption, isOptionTypeWithCode } from './guards';

type PossibleOptions<VALUE> =
  | string
  | OptionTypeWithCode<VALUE>
  | CreatableOption
  | (string | OptionTypeWithCode<VALUE> | CreatableOption)[];

type SingleChangeHandler<VALUE> = NonNullable<SelectProps<false, VALUE>['onChange']>;
type MultiChangeHandler<VALUE> = NonNullable<SelectProps<true, VALUE>['onChange']>;

type SingleOptionChangeHandler<VALUE> = NonNullable<SelectProps<false, VALUE>['onOptionChange']>;
type MultiOptionChangeHandler<VALUE> = NonNullable<SelectProps<true, VALUE>['onOptionChange']>;

export function useChangeHandler<VALUE>(
  multiple: boolean | undefined,
  onChange: SingleChangeHandler<VALUE> | MultiChangeHandler<VALUE> | undefined,
  onOptionChange: SingleOptionChangeHandler<VALUE> | MultiOptionChangeHandler<VALUE> | undefined,
  onCreate: SelectProps['onCreate'] | undefined
) {
  return useCallback(
    (options: PossibleOptions<VALUE> | null | undefined) => {
      const changeMulti = (options: OptionTypeWithCode<VALUE>[]) => {
        (onChange as MultiChangeHandler<VALUE>)?.(options.map((it) => it.value));
        (onOptionChange as MultiOptionChangeHandler<VALUE>)?.(options);
      };

      const changeSingle = (options: OptionTypeWithCode<VALUE> | undefined) => {
        (onChange as SingleChangeHandler<VALUE>)?.(options?.value);
        (onOptionChange as SingleOptionChangeHandler<VALUE>)?.(options);
      };

      if (!options || typeof options === 'string') {
        if (multiple) {
          changeMulti([]);
          return;
        }
        changeSingle(undefined);
        return;
      }

      if (!isArray(options)) {
        if (isCreatableOption(options)) {
          onCreate?.(options.creatableValue);
          return;
        }
        changeSingle(options);
        return;
      }
      if (isArray(options)) {
        const creatable = options.find(isCreatableOption);

        if (isCreatableOption(creatable)) {
          onCreate?.(creatable.creatableValue);
          return;
        }
        changeMulti(options.filter(isOptionTypeWithCode));
        return;
      }
    },
    [multiple, onChange, onCreate, onOptionChange]
  );
}
